import { BASE_URL } from "../../helper/constants";
import { convertBranchRole } from "../../helper/convertor";
import { stripMarkdownFromTitle } from "../../helper/stripMarkdownTitle";
import { isNativeVideoAttachment } from "../../utils/chatVideo";

const extractUrls = require("extract-urls");

/**
 * Pure helpers for the Pro News feed (web port of the mobile newsModel, branch upgrade/rn-0.87):
 * an article from /article/all or /company/getInternalArticle becomes the card model that
 * ArticleCardPro draws. Every value comes from a field the old UserFeed / CompanyFeed read.
 */

export const tr = (strings, key, fallback) => (strings && strings[key]) || fallback;
export const fill = (s, n) => String(s).replace("{0}", n);

// Legal forms the old card left out after a company name.
const PLAIN_FORMS = ["soleproprietorship", "sole proprietorship", "others", "einzelunternehmen", "einzelfirma", "andere"];

export const initialsOf = (name = "") =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

export const mediaUrl = (path) => {
  if (!path || typeof path !== "string") return null;
  return path.startsWith("http") || path.startsWith("blob:") || path.startsWith("data:") ? path : BASE_URL + path;
};

const dot = (...parts) => parts.map((p) => String(p || "").trim()).filter(Boolean).join(" · ");

/** Author name as the old cards built it (full name, or poster name + legal form). */
export function authorName(item = {}, internal = false) {
  if (internal && !item.userFullName) return String(item.postedByName || "").trim();
  if (item.articleUserTitle && item.createdBy === "user") return String(item.userFullName || "").trim();
  const base = item.userFullName ? String(item.userFullName).trim() : String(item.postedByName || "").trim();
  const form = item.corporateForm ? String(item.corporateForm) : "";
  return form && !PLAIN_FORMS.includes(form.toLowerCase()) ? `${base} ${form}` : base;
}

/** Publish time in ms: publishedAt (seconds) when set, else createdAt. */
export function articleTime(item = {}) {
  const published = Number(item.publishedAt);
  if (published > 0) return published * 1000;
  const created = item.createdAt ? new Date(item.createdAt).getTime() : NaN;
  return Number.isNaN(created) ? null : created;
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Card time as the design prints it: "now", "12 min ago", "2 h ago", "Yesterday", the weekday
 * within a week ("Mon"), then "3 Oct" (or "3 Oct 2025" in another year).
 */
export function timeAgo(ms, { strings, locale = "de-DE", now = Date.now() } = {}) {
  if (!ms) return "";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  const n = new Date(now);
  const mins = Math.floor((n.getTime() - d.getTime()) / 60000);
  if (mins >= 0) {
    if (mins < 1) return tr(strings, "pro_news_time_now", "now");
    if (mins < 60) return fill(tr(strings, "pro_news_time_minutes", "{0} min ago"), mins);
    if (sameDay(d, n)) return fill(tr(strings, "pro_news_time_hours", "{0} h ago"), Math.floor(mins / 60));
    const yesterday = new Date(n);
    yesterday.setDate(n.getDate() - 1);
    if (sameDay(d, yesterday)) return tr(strings, "cockpit_yesterday", "Yesterday");
    if (mins < 7 * 24 * 60) return d.toLocaleDateString(locale, { weekday: "short" });
  }
  const opts = d.getFullYear() === n.getFullYear() ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" };
  return d.toLocaleDateString(locale, opts);
}

const ENTITIES = { "&nbsp;": " ", "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&apos;": "'" };

/** Article HTML as plain text (the card's 3-line body): block ends become breaks. */
export function plainText(html) {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<[^>]*>/g, "")
    .replace(/&(nbsp|amp|lt|gt|quot|#39|apos);/g, (m) => ENTITIES[m] || m)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const BULLET_LINE = /^[•·▪●◦‣\-–*]\s+(.*)$/;
const NUMBERED_LINE = /^(\d{1,3})[.)]\s+(.*)$/;

/**
 * The plain body as blocks for the card: every line becomes a paragraph, lines starting with a
 * bullet ("•", "-", "*") or a number ("1.", "2)") become list items. Blank lines only separate.
 */
export function bodyBlocks(plain) {
  if (!plain) return [];
  return plain
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const bullet = line.match(BULLET_LINE);
      if (bullet) return { kind: "li", mark: "•", text: bullet[1] };
      const numbered = line.match(NUMBERED_LINE);
      if (numbered) return { kind: "li", mark: `${numbered[1]}.`, text: numbered[2] };
      return { kind: "p", text: line };
    });
}

/**
 * Header of a card (KPNews: 38 logo tile, name, "author · time").
 *   internal feed   the selected company (name + logo) and the employee who posted it
 *   company post    the company (name + legal form, its logo) and the employee who posted it
 *   personal post   the person (round avatar) and their role
 */
export function proHead(item = {}, { internal = false, company = null, isGerm, strings, locale } = {}) {
  const time = timeAgo(articleTime(item), { strings, locale });
  if (internal && company && company.name) {
    return { name: company.name, sub: dot(item.postedByName, time), kind: "company", logoUri: mediaUrl(company.logo), initials: initialsOf(company.name) };
  }
  if (item.createdBy === "company") {
    const name = authorName(item, false);
    const poster = item.postedById != null && String(item.postedByName || "").trim() ? String(item.postedByName).trim() : null;
    return {
      name,
      sub: dot(item.postedByName, time),
      poster,
      time,
      kind: "company",
      logoUri: mediaUrl(item.logo || item.avatar),
      initials: initialsOf(name),
    };
  }
  const name = authorName(item, internal);
  const firstRole = String(item.buildingIndustryRole || "").split(",")[0].trim();
  const role = firstRole ? convertBranchRole(firstRole, isGerm) || "" : "";
  return {
    name,
    sub: dot(role, time),
    kind: "person",
    logoUri: mediaUrl(internal ? item.postedByImage || item.avatar : item.avatar || item.postedByImage),
    initials: initialsOf(name),
  };
}

/** The card model: header, title, plain body, like state, department chips and product. */
export function toProCard(item = {}, opts = {}) {
  const internal = !!opts.internal;
  const tags =
    internal && Array.isArray(item.deptLocation)
      ? item.deptLocation.filter((d) => d && d.departmentLocation).map((d, i) => ({ key: String(d.deptLocation ?? i), label: d.departmentLocation }))
      : [];
  const product = item.product && item.product !== "null" ? item.product : "";
  return {
    id: item.id,
    head: proHead(item, opts),
    title: stripMarkdownFromTitle(item.title || "") || "",
    plain: plainText(typeof item.description === "string" ? item.description : ""),
    likeCount: Number(item.likeCount) || 0,
    liked: !!item.likeStatus,
    likeDisabled: item.postedById != null && opts.userId != null && String(item.postedById) === String(opts.userId),
    tags,
    product,
  };
}

/* ---- Media ---------------------------------------------------------------------------- */

export function isPdfItem(item) {
  if (!item) return false;
  if (item.isPdf || item.type === "application/pdf") return true;
  const candidates = [item.fileUrl, item.href, item.url, item.name, item.attachedFileName];
  return candidates.some((c) => typeof c === "string" && /\.pdf(\?|$)/i.test(c));
}

export function pdfUrlOf(item) {
  if (!item) return "";
  if (item.url && (item.url.startsWith("blob:") || item.url.startsWith("http"))) return item.url;
  if (item.href) return item.href.startsWith("http") ? item.href : BASE_URL + item.href;
  const furl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
  if (furl) return furl.startsWith("http") || furl.startsWith("blob:") ? furl : BASE_URL + furl;
  return "";
}

export function pdfNameOf(item) {
  if (!item) return "document.pdf";
  if (item.attachedFileName) return String(item.attachedFileName).split("?")[0];
  if (item.originalName) return String(item.originalName).split("?")[0];
  if (item.name && !/^[0-9a-f-]{30,}\.pdf$/i.test(item.name)) return String(item.name).split("?")[0];
  return "document.pdf";
}

export function imageUrlOf(item) {
  if (!item) return "";
  if (item.url && (String(item.url).startsWith("blob:") || String(item.url).startsWith("data:"))) return item.url;
  if (item.href) return item.href.startsWith("http") ? item.href : BASE_URL + item.href;
  const furl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
  if (!furl) return item.url || "";
  return furl.startsWith("http") || furl.startsWith("blob:") || furl.startsWith("data:") ? furl : BASE_URL + furl;
}

export function youtubeIdOf(href) {
  const m = String(href || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

/** Article media as the old cards built it: YouTube / Vimeo links from the body, then the uploads. */
export function mediaOf(item = {}) {
  const uploads = Array.isArray(item.uploadUrl) ? item.uploadUrl : [];
  if (!item.description || typeof item.description !== "string") return [...uploads];
  let urls = extractUrls(item.description);
  urls = Array.isArray(urls) ? urls.map((e) => e.replace("&quot", "")) : [];
  const links = [];
  [...new Set(urls)].forEach((href) => {
    if (href.match("vimeo")) links.push({ href, videoType: "vimeo" });
    if (href.match("youtube") || href.match("youtu")) links.push({ href, videoType: "youtube" });
  });
  return [...links, ...uploads];
}

/** Tile kind of one media item. */
export function tileOf(m = {}) {
  if (isPdfItem(m)) return "pdf";
  if (m.videoType === "youtube") return "youtube";
  if (m.videoType) return "embed";
  if (isNativeVideoAttachment(m)) return "video";
  return "image";
}

/** Splits media into the 16:9 pager (photos, videos, links) and the PDF attachments. */
export function splitMedia(media = []) {
  const visual = [];
  const files = [];
  (media || []).forEach((m, index) => {
    const kind = tileOf(m);
    if (kind === "pdf") files.push({ index, name: pdfNameOf(m), url: pdfUrlOf(m), item: m });
    else visual.push({ index, kind, item: m });
  });
  return { visual, files };
}
